import { HttpService } from "../http"
import { ApiRoutes } from "../routes"
import { SessionService } from "../session"
import { Logger } from "../logger"
import { LoginError } from "../../models/login"
import { FranchiseUsers } from "../../models/common/FranchiseUsers"
import { CreateFranchiseUsers } from "../../models/transport/request/CreateFranchiseUsers"
import { SupportUserBriefResponse } from "../../models/transport/response/supportUserBrief"
import { UpdateSupportUserRequest } from "../../models/transport/request/UpdateSupportUserRequest"
import { container } from "../../../infrastructure/container"
import { FranchiseUserBriefResponse } from "../../models/transport/response/FranchiseUserBriefResponse"

/**
 * Interface defining CRUD operations for Support Users.
 */
export interface FranchiseUserService {
    /** Create a new Franchise user. */
    createFranchiseUsers(request: CreateFranchiseUsers): Promise<FranchiseUsers>

    /** Get Franchise users. */
    getFranchiseUsers(franchiseId: string): Promise<FranchiseUserBriefResponse[]>

    /** Delete Franchise users. */
    deleteSupportUsers(email: string): Promise<{ message: string }>

    /** Update Franchise users. */
    updateFranchiseUser(data: CreateFranchiseUsers): Promise<FranchiseUserBriefResponse>
}

/**
 * Implementation of FranchiseUserService that interacts with the backend API.
 */
export class AppFranchiseUserService implements FranchiseUserService {
    /**
     * Initializes the login service with the HTTP service instance.
     * @param httpService - The HTTP service to send API requests.
     */
    private httpService = container.resolve<HttpService>("httpService")
    private sessionService = container.resolve<SessionService>("sessionService")
    private logger = container.resolve<Logger>("logger")

    // -------------------------------------------------------------
    // CREATE
    // -------------------------------------------------------------
    async createFranchiseUsers(request: CreateFranchiseUsers): Promise<FranchiseUsers> {
        try {
            const created = await this.httpService.post<
                CreateFranchiseUsers,
                FranchiseUsers,
                LoginError
            >(ApiRoutes.CREATE_FRANCHISE_USER, request)
            console.log(created)
            return created
        } catch (error) {
            this.logger.error("Error creating franchise user:", error)
            throw error
        }
    }

    // -------------------------------------------------------------
    // Get
    // -------------------------------------------------------------
    async getFranchiseUsers(franchiseId: string): Promise<FranchiseUserBriefResponse[]> {
        try {

            const fullUsers = await this.httpService.get<FranchiseUsers[], LoginError>(
                `${ApiRoutes.GET_FRANCHISE_USER}/${franchiseId}`
            )
            // Convert to brief model – use email as a stable unique key
            const briefList: FranchiseUserBriefResponse[] = fullUsers.map((u) => ({
                id: u.email,
                franchiseId: u.franchiseId,
                firstname: u.firstname,
                lastname: u.lastname,
                email: u.email,
                phone: typeof u.phone === "string"
                    ? { primary: u.phone, secondary: "" }
                    : u.phone,
                role: u.role,
                terminals: Array.isArray(u.terminals)
                    ? u.terminals.map((t: any) =>
                        typeof t === "string"
                            ? { terminalId: t, name: "Unknown" }
                            : { terminalId: t.terminalId, name: t.name ?? "Unknown" }
                    )
                    : [],
                address: u.address,
                password: (u as any).password ?? "",
                isActive: u.isActive ?? true,
            }))

            this.sessionService.setFranchiseUserBrief(briefList)
            console.log(briefList)
            return briefList
        } catch (error) {
            this.logger.error("Error fetching support users:", error)
            throw error
        }
    }

    // -------------------------------------------------------------
    // Delete
    // -------------------------------------------------------------
    async deleteSupportUsers(email: string): Promise<{ message: string }> {
        try {
            const route = `${ApiRoutes.DELETE_FRANCHISE_USER}?email=${encodeURIComponent(email)}`
            const response = await this.httpService.delete<{ message: string }, LoginError>(route)
            this.logger.info("Franchise User Deleted:", response.message)
            return response
        } catch (error) {
            this.logger.error("Error Deleting Franchise User:", error)
            throw error
        }
    }

    // -------------------------------------------------------------
    // Update
    // -------------------------------------------------------------
    async updateFranchiseUser(data: CreateFranchiseUsers): Promise<FranchiseUserBriefResponse> {
        try {
            const updated = await this.httpService.post<
                CreateFranchiseUsers,
                FranchiseUserBriefResponse,
                LoginError
            >(ApiRoutes.UPDATE_FRANCHISE_USER, data)
            console.log(updated)
            return updated
        } catch (error) {
            this.logger.error("Error updating franchise user:", error)
            throw error
        }
    }
}